/**
 * Chore completion helpers — Phase 2 of [[tasks/daily-chore-reset]].
 *
 * Date-aware "is done now?" predicate + transactional event-log writer +
 * undo + lifetime-points aggregator. Mirrors the server-side model that
 * shipped in [[tasks/daily-chore-reset]] Phase 1.
 *
 * Daily chores reset at UTC midnight via the reader predicate — no
 * scheduled function. Non-recurring chores stay done indefinitely once
 * any event exists for them. Legacy `isCompleted: true` is honored as a
 * fallback during the rollout window; the fallback branch is removed in
 * Phase 5 once telemetry shows zero legacy reads.
 */

import {
  collection,
  deleteDoc,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type {
  AssignedChore,
  ChoreCompletionEvent,
} from "@/lib/models";

export function utcDateString(date: Date): string {
  const yyyy = date.getUTCFullYear().toString().padStart(4, "0");
  const mm = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = date.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function eventIdFor(choreUid: string, date: Date): string {
  return `${choreUid}_${utcDateString(date)}`;
}

/**
 * "Is this chore done **right now**?" — the predicate readers should use
 * everywhere instead of filtering on `isCompleted`.
 *
 * Logic:
 *   - `completed_at` set:
 *       * daily / weekdays → only when on today's UTC day
 *       * weekly           → only within the current calendar week
 *       * monthly          → only within the current calendar month
 *       * other / nil      → always done (one-shot chore)
 *   - `completed_at` absent:
 *       * recurring chore  → false (legacy completion has no date; safer
 *                            to reset to pending for the current period)
 *       * non-recurring    → legacy fallback to `isCompleted === true`
 *
 * Recurrence is matched case-insensitively — production data uses
 * "Daily" / "Weekly" / "Weekdays" (capitalized) for chores.
 */
export function isDoneNow(
  chore: Pick<AssignedChore, "isCompleted" | "completed_at" | "recurrence">,
  now: Date = new Date(),
): boolean {
  const recurrence = chore.recurrence?.toLowerCase();
  const isRecurring =
    recurrence === "daily" ||
    recurrence === "weekdays" ||
    recurrence === "weekly" ||
    recurrence === "monthly";

  const completedAt = chore.completed_at;
  if (completedAt) {
    const completedDate = completedAt.toDate();
    switch (recurrence) {
      case "daily":
      case "weekdays":
        return utcDateString(completedDate) === utcDateString(now);
      case "weekly": {
        const cs = startOfWeek(completedDate);
        const ns = startOfWeek(now);
        return cs.getTime() === ns.getTime();
      }
      case "monthly":
        return (
          completedDate.getUTCFullYear() === now.getUTCFullYear() &&
          completedDate.getUTCMonth() === now.getUTCMonth()
        );
      default:
        return true;
    }
  }
  // Legacy chore (no completed_at). Recurring → reset to pending.
  if (isRecurring) return false;
  return chore.isCompleted === true;
}

/** Sunday-start UTC week boundary. Returns the Date at 00:00 UTC of the
 *  Sunday on/before `date`. */
function startOfWeek(date: Date): Date {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d;
}

interface MarkInput {
  chore: AssignedChore;
  /** Who the points/event credits (the chore's assignee). */
  assigneeUid: string;
  /** Who actually tapped the checkbox (could be an adult marking a kid's chore). */
  completerUid: string;
}

/**
 * Idempotent "mark chore complete" — creates a `chore_completions` event
 * for today's UTC day and stamps `completed_at` + `isCompleted` on the
 * parent chore for legacy reader compatibility.
 *
 * If a completion event already exists for today, this is a no-op (returns
 * `false`). Otherwise creates the event + updates the parent atomically
 * and returns `true`.
 *
 * Throws on permission / network errors — caller should `try/catch` and
 * show a toast.
 */
export async function markChoreComplete({
  chore,
  assigneeUid,
  completerUid,
}: MarkInput): Promise<boolean> {
  if (!chore.uid) throw new Error("markChoreComplete: chore.uid required");
  if (!chore.household_id)
    throw new Error("markChoreComplete: chore.household_id required");

  const now = new Date();
  const date = utcDateString(now);
  const eventId = `${chore.uid}_${date}`;

  const eventRef = doc(
    db,
    `households/${chore.household_id}/chore_completions/${eventId}`,
  );
  const choreRef = doc(
    db,
    `member_profiles/${assigneeUid}/assigned_chores/${chore.uid}`,
  );

  return await runTransaction(db, async (tx) => {
    const existing = await tx.get(eventRef);
    if (existing.exists()) return false; // already completed today — no-op

    const eventData: Omit<ChoreCompletionEvent, "uid" | "completed_at"> & {
      completed_at: ReturnType<typeof serverTimestamp>;
    } = {
      chore_uid: chore.uid!,
      member_id: assigneeUid,
      household_id: chore.household_id,
      completed_at: serverTimestamp(),
      date,
      points_earned: chore.points ?? 0,
      tip_points_earned: chore.tip_points ?? 0,
      completed_by: completerUid,
    };
    tx.set(eventRef, eventData);
    tx.update(choreRef, {
      isCompleted: true,
      completed_at: serverTimestamp(),
      status: "done",
    });
    return true;
  });
}

interface UndoInput {
  chore: AssignedChore;
  assigneeUid: string;
}

/**
 * Undo today's completion. Deletes the event and clears `completed_at` +
 * `isCompleted` on the parent. Past-day events are immutable — undoing
 * yesterday's completion is rejected (returns `false`).
 */
export async function undoChoreCompletion({
  chore,
  assigneeUid,
}: UndoInput): Promise<boolean> {
  if (!chore.uid) throw new Error("undoChoreCompletion: chore.uid required");
  if (!chore.household_id)
    throw new Error("undoChoreCompletion: chore.household_id required");

  const now = new Date();
  const todayDate = utcDateString(now);
  const eventId = `${chore.uid}_${todayDate}`;

  const eventRef = doc(
    db,
    `households/${chore.household_id}/chore_completions/${eventId}`,
  );
  const choreRef = doc(
    db,
    `member_profiles/${assigneeUid}/assigned_chores/${chore.uid}`,
  );

  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(eventRef);
    if (!snap.exists()) return false; // nothing to undo
    if (snap.data().date !== todayDate) return false; // past day — locked

    tx.delete(eventRef);
    tx.update(choreRef, {
      isCompleted: false,
      completed_at: null,
      status: "todo",
    });
    return true;
  });
}

/**
 * Toggle convenience — if the chore reads done-now, undo; else mark.
 * Returns `true` if the toggle resulted in a state change.
 */
export async function toggleChoreCompletion(
  input: MarkInput,
  now: Date = new Date(),
): Promise<boolean> {
  if (isDoneNow(input.chore, now)) {
    return undoChoreCompletion({
      chore: input.chore,
      assigneeUid: input.assigneeUid,
    });
  }
  return markChoreComplete(input);
}

/**
 * Sum lifetime earned points across a list of `chore_completions` events
 * (typically scoped to a member via `where("member_id", "==", uid)`).
 */
export function totalEarnedPoints(
  events: Pick<ChoreCompletionEvent, "points_earned" | "tip_points_earned">[],
): number {
  return events.reduce(
    (acc, e) => acc + (e.points_earned ?? 0) + (e.tip_points_earned ?? 0),
    0,
  );
}

/**
 * Firestore path helper — gives the collection path for a household's
 * completion event log. Pass to `useFirestoreCollection<ChoreCompletionEvent>`.
 */
export function choreCompletionsPath(householdId: string): string {
  return `households/${householdId}/chore_completions`;
}

/**
 * Active-household filter for the raw cross-household
 * `member_profiles/{uid}/assigned_chores` subcollection. The chores
 * screen is inherently household-scoped — switching households swaps
 * the chore list context, and the `chore_completions` listener is also
 * scoped per-active-household, so both must agree.
 *
 * Mirrors iOS PR #111 + Android `feat/android-rewards-firestore`
 * commit `68292ee`. See [[tasks/daily-chore-reset]].
 */
export function filterAssignedChoresByHousehold<
  T extends { household_id?: string | null },
>(chores: T[], householdId: string | null | undefined): T[] {
  if (!householdId) return [];
  return chores.filter((c) => c.household_id === householdId);
}

// Avoid unused-import warnings when collection helper isn't used here yet —
// kept for future surfaces that need it.
void collection;
void deleteDoc;
